import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutHeroUrl = 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20office%20interior%20with%20soft%20natural%20light%2C%20cream%20and%20warm%20beige%20tones%2C%20collaborative%20workspace%2C%20clean%20aesthetic%2C%20professional%20atmosphere%2C%20editorial%20photography&width=1800&height=600&seq=about-hero&orientation=landscape';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '网页版免费91',
  url: 'https://avvg.cn',
  description: '无需下载安装即可直接在浏览器中免费使用的在线平台',
  foundingDate: '2022',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@mianfei91.com',
    availableLanguage: ['Chinese'],
    hoursAvailable: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CN',
    addressLocality: '北京',
  },
};

const stats = [
  { value: '500万+', label: '累计用户' },
  { value: '200+', label: '在线工具' },
  { value: '99.9%', label: '服务可用率' },
  { value: '50万+', label: '日均访问' },
];

const milestones = [
  { year: '2022年', event: '网页版免费91正式上线，推出首批20款在线工具' },
  { year: '2023年', event: '平台用户突破100万，新增教育学习和娱乐休闲两大分类' },
  { year: '2024年', event: '累计用户超过500万，工具数量突破200款，完成品牌全面升级' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9te9d1anrhn4bgf7gdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '关于我们 - 网页版免费91',
        description: '了解网页版免费91的品牌故事、发展历程和联系方式',
        url: 'https://avvg.cn/about',
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Page Header */}
          <section className="relative h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={aboutHeroUrl}
              alt="关于网页版免费91"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4">
                <h1 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">关于我们</h1>
                <p className="text-sm md:text-base text-white/70 max-w-lg mx-auto">
                  了解网页版免费91的品牌故事与使命
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">品牌介绍</h2>
              <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-4">
                <p>
                  网页版免费91成立于2022年，是国内领先的免费在线工具平台。我们致力于为用户提供无需下载安装、即开即用的在线服务体验。
                  无论是文档处理、在线娱乐、学习教育还是办公效率，网页版免费91都能为您提供一站式的解决方案。
                </p>
                <p>
                  我们的团队由一群充满激情的技术专家和产品设计师组成，大家怀着让互联网工具更简单、更易用的共同愿景走到了一起。
                  我们相信，优秀的工具应该像打开浏览器一样简单，而不是让用户在复杂的安装和配置中浪费时间。
                </p>
                <p>
                  经过两年的快速发展，网页版免费91已经从最初的20款工具扩展到超过200款在线工具，覆盖六大核心分类。
                  我们累计服务超过500万用户，日均访问量突破50万次。这些数字的背后，是用户对我们平台的信任和认可。
                </p>
                <p>
                  <strong>我们的使命</strong>：让每一个人都能零门槛地享受互联网工具带来的便利。
                </p>
                <p>
                  <strong>我们的愿景</strong>：成为全球用户首选的免费在线工具平台，让在线服务触手可及。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-primary-600 font-heading mb-2">{stat.value}</p>
                    <p className="text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">发展历程</h2>
              <div className="flex flex-col gap-6">
                {milestones.map((item, index) => (
                  <div key={index} className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-20 pt-1">
                      <span className="text-sm font-semibold text-primary-600 font-heading">{item.year}</span>
                    </div>
                    <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Contact Info */}
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-6 font-heading">联系方式</h3>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</p>
                        <p className="text-sm text-foreground-600">contact@mianfei91.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-customer-service-2-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 mb-1">在线客服</p>
                        <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-map-pin-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 mb-1">公司地址</p>
                        <p className="text-sm text-foreground-600">中国北京市朝阳区科技园区</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-wechat-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900 mb-1">微信公众号</p>
                        <p className="text-sm text-foreground-600">网页版免费91</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-6 font-heading">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="true" id="about-contact-form" className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}